/* src/pages/KnowledgeBaseLibrary.jsx
 * Workspace Knowledge Base library — list, edit, and create KBs.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection, query, where, getDocs, addDoc, updateDoc, doc,
} from 'firebase/firestore';
import { db } from '../firebase';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from '../contexts/ToastContext';
import InlineConfirm from '../components/InlineConfirm';
import { scrapeWebsite } from '../utils/api';
import {
  assembleGuidedContent,
  countAnsweredFields,
  emptyGuidedAnswers,
  guidedDisplayLabel,
  guidedFieldTotal,
  hasGuidedContent,
  TONE_CHIPS,
} from '../utils/kbAssemble';
import {
  kbSummary,
  splitKbBodyParagraphs,
  composeOverview,
  getParsedChatOverview,
  parseKbContent,
} from '../utils/kbContent';
import { KbTableSkeleton } from '../components/Skeleton';

const BODY_COLLAPSE_CHARS = 600;
const BODY_COLLAPSE_PARAS = 3;

const SUBTITLE = 'The knowledge your AI uses to reply to leads, filter spam, and answer chats. Build one here and reuse it across forms and your chat widget.';

function formatTimestamp(value) {
  if (!value) return 'Unknown';
  const d = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(d.getTime())) return 'Unknown';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function KbUsageChips({ formCount, chatCount, isUnused, hasChatContent }) {
  return (
    <>
      <span className="inbox-chip">
        {formCount} form{formCount === 1 ? '' : 's'} · {chatCount} chat{chatCount === 1 ? '' : 's'}
      </span>
      {isUnused && <span className="inbox-chip warn">Unused</span>}
      {hasChatContent && <span className="inbox-chip accent-soft">Chat-ready</span>}
    </>
  );
}

function KbRowActions({ kb, onView, onEdit }) {
  return (
    <>
      <button
        type="button"
        className="inbox-pill-btn"
        onClick={() => onView(kb)}
      >
        View
      </button>
      <button
        type="button"
        className="inbox-pill-btn-primary"
        onClick={() => onEdit(kb)}
      >
        Edit
      </button>
    </>
  );
}

function useModalEscape(onClose, active) {
  useEffect(() => {
    if (!active) return undefined;
    function handleEscape(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [active, onClose]);
}

function KbSectionedBody({ content, excludeSources = false }) {
  const parsed = parseKbContent(content || '');

  if (parsed.isGuided && parsed.sections.length > 0) {
    const visibleSections = excludeSources
      ? parsed.sections.filter((section) => section.label !== 'Sources:')
      : parsed.sections;

    return (
      <div>
        {visibleSections.map((section) => (
          <div key={section.label} style={{ marginBottom: '1.25rem' }}>
            <p className="fb-label" style={{ marginBottom: '0.5rem' }}>
              {guidedDisplayLabel(section.label)}
            </p>
            <CollapsibleText text={section.body} />
          </div>
        ))}
      </div>
    );
  }

  const overview = composeOverview(parsed);

  return (
    <div>
      {overview && (
        <div style={{ marginBottom: '1.25rem' }}>
          <p className="fb-label" style={{ marginBottom: '0.5rem' }}>Overview</p>
          <CollapsibleText text={overview} />
        </div>
      )}
      {!parsed.isStructured && parsed.headings.length > 0 && (
        <div style={{ marginBottom: '1.25rem' }}>
          <p className="fb-label" style={{ marginBottom: '0.5rem' }}>Topics on the page</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {parsed.headings.map((heading) => (
              <span key={heading} className="inbox-chip">{heading}</span>
            ))}
          </div>
        </div>
      )}
      {!parsed.isStructured && parsed.body && (
        <div style={{ marginBottom: '1.25rem' }}>
          <p className="fb-label" style={{ marginBottom: '0.5rem' }}>Details</p>
          <CollapsibleText text={parsed.body} />
        </div>
      )}
    </div>
  );
}

function CollapsibleText({ text, paragraphs, collapseChars = BODY_COLLAPSE_CHARS, collapseParas = BODY_COLLAPSE_PARAS }) {
  const [expanded, setExpanded] = useState(false);
  const parts = paragraphs || splitKbBodyParagraphs(text);
  const totalChars = parts.join(' ').length;
  const needsCollapse = totalChars > collapseChars || parts.length > collapseParas;

  const visibleParts = expanded || !needsCollapse
    ? parts
    : parts.slice(0, collapseParas);

  if (!text && !parts.length) return null;

  return (
    <div>
      {visibleParts.map((part, index) => (
        <p
          key={`${index}-${part.slice(0, 24)}`}
          style={{ margin: '0 0 12px', color: 'var(--ink)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
        >
          {part}
        </p>
      ))}
      {needsCollapse && (
        <button
          type="button"
          className="inbox-pill-btn"
          onClick={() => setExpanded((v) => !v)}
          style={{ marginTop: '4px' }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function KbViewModal({ kb, onClose }) {
  const parsed = parseKbContent(kb.content || '');
  const hasChatContent = typeof kb.chatContent === 'string' && kb.chatContent.trim().length > 0;
  const chatOverview = hasChatContent ? getParsedChatOverview(kb.chatContent) : '';

  return (
    <div
      className="fb-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="fb-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kb-view-title"
        style={{ maxWidth: '720px', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fb-modal-header">
          <h2 id="kb-view-title" className="fb-modal-title">
            {kb.name || 'Unnamed knowledge base'}
          </h2>
        </div>

        <KbSectionedBody content={kb.content || ''} excludeSources />

        <div style={{ marginBottom: '1.5rem' }}>
          <p className="fb-label" style={{ marginBottom: '0.5rem' }}>Chat-specific knowledge</p>
          {hasChatContent ? (
            <CollapsibleText text={chatOverview} />
          ) : (
            <p style={{ margin: 0, color: 'var(--muted)', lineHeight: 1.5 }}>
              Chat uses the knowledge above.
            </p>
          )}
        </div>

        {parsed.sources.length > 0 && (
          <div style={{ marginBottom: '1.5rem' }}>
            <p className="fb-label" style={{ marginBottom: '0.5rem' }}>Sources</p>
            <ul style={{ margin: 0, paddingLeft: '1.25rem', color: 'var(--muted)', fontSize: '0.85rem', lineHeight: 1.5 }}>
              {parsed.sources.map((url) => (
                <li key={url} style={{ wordBreak: 'break-all' }}>{url}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="fb-actions-row">
          <button type="button" className="inbox-pill-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function GuidedField({ id, label, helper, placeholder, value, onChange, rows = 3 }) {
  return (
    <div style={{ marginBottom: '1.25rem' }}>
      <label htmlFor={id} className="fb-label">{label}</label>
      <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>{helper}</p>
      <textarea
        id={id}
        className="fb-textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
      />
    </div>
  );
}

function GuidedCreateForm({
  answers,
  setAnswers,
  pullingLinkId,
  onPullLink,
}) {
  const answered = countAnsweredFields(answers);
  const total = guidedFieldTotal();

  function updateField(field, value) {
    setAnswers((prev) => ({ ...prev, [field]: value }));
  }

  function appendTone(chip) {
    setAnswers((prev) => {
      const current = prev.tone.trim();
      if (!current) return { ...prev, tone: chip };
      if (current.toLowerCase().includes(chip.toLowerCase())) return prev;
      return { ...prev, tone: `${current}, ${chip}` };
    });
  }

  function addFaqRow() {
    setAnswers((prev) => ({
      ...prev,
      faq: [...prev.faq, { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, question: '', answer: '' }],
    }));
  }

  function updateFaqRow(id, field, value) {
    setAnswers((prev) => ({
      ...prev,
      faq: prev.faq.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  }

  function removeFaqRow(id) {
    setAnswers((prev) => ({
      ...prev,
      faq: prev.faq.length <= 1
        ? [{ id: `${Date.now()}`, question: '', answer: '' }]
        : prev.faq.filter((row) => row.id !== id),
    }));
  }

  function addReferenceLink(url) {
    const trimmed = url.trim();
    if (!trimmed) return;
    setAnswers((prev) => ({
      ...prev,
      referenceLinks: [
        ...prev.referenceLinks,
        { id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, url: trimmed, pulledContent: '' },
      ],
    }));
  }

  function removeReferenceLink(id) {
    setAnswers((prev) => ({
      ...prev,
      referenceLinks: prev.referenceLinks.filter((link) => link.id !== id),
    }));
  }

  const [linkInput, setLinkInput] = React.useState('');

  return (
    <div>
      <p className="fb-muted" style={{ fontSize: '0.85rem', marginBottom: '1rem' }}>
        {answered} of {total} answered. Every question is optional.
      </p>

      <GuidedField
        id="kb-guided-overview"
        label="What does your business do?"
        helper="A short overview in one or two sentences."
        placeholder="e.g. We help local homeowners install solar panels with same-week scheduling."
        value={answers.overview}
        onChange={(v) => updateField('overview', v)}
        rows={2}
      />

      <GuidedField
        id="kb-guided-offerings"
        label="What do you offer?"
        helper="List services or products, one per line."
        placeholder={'Web design\nSEO retainers\nMonthly reporting'}
        value={answers.offerings}
        onChange={(v) => updateField('offerings', v)}
      />

      <GuidedField
        id="kb-guided-pricing"
        label="Pricing"
        helper="Ranges, starting prices, or free consult offers. Skip if you prefer not to share."
        placeholder="e.g. Projects start at $2,500. Free 15-minute consult."
        value={answers.pricing}
        onChange={(v) => updateField('pricing', v)}
        rows={2}
      />

      <GuidedField
        id="kb-guided-area"
        label="Where and when"
        helper="Service area, location, hours, or online only."
        placeholder="e.g. Austin metro, Mon-Fri 8am-6pm. Online consultations nationwide."
        value={answers.areaAndHours}
        onChange={(v) => updateField('areaAndHours', v)}
        rows={2}
      />

      <div style={{ marginBottom: '1.25rem' }}>
        <label htmlFor="kb-guided-tone" className="fb-label">Tone and voice</label>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
          How should the AI sound when it replies?
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '0.5rem' }}>
          {TONE_CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              className="inbox-chip"
              onClick={() => appendTone(chip)}
              style={{ cursor: 'pointer' }}
            >
              {chip}
            </button>
          ))}
        </div>
        <textarea
          id="kb-guided-tone"
          className="fb-textarea"
          value={answers.tone}
          onChange={(e) => updateField('tone', e.target.value)}
          placeholder="e.g. Friendly and confident. Keep replies short."
          rows={2}
        />
      </div>

      <GuidedField
        id="kb-guided-about"
        label="About you / background"
        helper="Story, founding, team, credentials. What builds trust?"
        placeholder="e.g. Founded in 2018 by two former agency owners..."
        value={answers.about}
        onChange={(v) => updateField('about', v)}
      />

      <GuidedField
        id="kb-guided-notable"
        label="Notable things and news"
        helper="Awards, milestones, press, recent wins, or case studies."
        placeholder="e.g. Named Best Local Installer 2025. Featured in Austin Business Journal."
        value={answers.notable}
        onChange={(v) => updateField('notable', v)}
      />

      <div style={{ marginBottom: '1.25rem' }}>
        <div className="fb-header-row" style={{ marginBottom: '0.5rem' }}>
          <label className="fb-label" style={{ marginBottom: 0 }}>Common questions</label>
          <button type="button" className="inbox-pill-btn" onClick={addFaqRow}>
            + Add question
          </button>
        </div>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
          Q and A pairs are the highest-value input for replies and chat.
        </p>
        {answers.faq.map((row, index) => (
          <div key={row.id} className="fb-callout info" style={{ marginBottom: '0.75rem' }}>
            <p className="fb-label" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
              Question {index + 1}
            </p>
            <input
              type="text"
              className="fb-input"
              value={row.question}
              onChange={(e) => updateFaqRow(row.id, 'question', e.target.value)}
              placeholder="e.g. Do you offer emergency service?"
              style={{ width: '100%', marginBottom: '0.5rem' }}
            />
            <textarea
              className="fb-textarea"
              value={row.answer}
              onChange={(e) => updateFaqRow(row.id, 'answer', e.target.value)}
              placeholder="e.g. Yes, we offer same-day emergency visits."
              rows={2}
              style={{ marginBottom: '0.5rem' }}
            />
            <button type="button" className="fb-icon-remove" onClick={() => removeFaqRow(row.id)}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '1.25rem' }}>
        <label className="fb-label">Reference links (any URL)</label>
        <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.75rem' }}>
          These do not have to be your website. News, press, or any page that describes you works.
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
          <input
            type="url"
            className="fb-input"
            value={linkInput}
            onChange={(e) => setLinkInput(e.target.value)}
            placeholder="https://news.example.com/your-story"
            style={{ flex: 1, minWidth: '200px' }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && linkInput.trim()) {
                addReferenceLink(linkInput);
                setLinkInput('');
              }
            }}
          />
          <button
            type="button"
            className="inbox-pill-btn-primary"
            onClick={() => {
              addReferenceLink(linkInput);
              setLinkInput('');
            }}
            disabled={!linkInput.trim()}
            style={{ opacity: linkInput.trim() ? 1 : 0.6 }}
          >
            Add link
          </button>
        </div>
        {answers.referenceLinks.map((link) => (
          <div key={link.id} className="fb-list-row">
            <span style={{ fontSize: '0.85rem', color: 'var(--ink)', wordBreak: 'break-all' }}>{link.url}</span>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={() => onPullLink(link.id, link.url)}
                disabled={pullingLinkId === link.id}
                style={{ opacity: pullingLinkId === link.id ? 0.6 : 1 }}
              >
                {pullingLinkId === link.id ? 'Pulling...' : 'Pull content'}
              </button>
              <button type="button" className="fb-icon-remove" onClick={() => removeReferenceLink(link.id)}>
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>

      <GuidedField
        id="kb-guided-booking"
        label="How should a lead book / next step?"
        helper="The call to action the AI should drive toward."
        placeholder="e.g. Book a free consult at example.com/book or reply with your phone number."
        value={answers.booking}
        onChange={(v) => updateField('booking', v)}
        rows={2}
      />

      <GuidedField
        id="kb-guided-guardrails"
        label="Anything the AI should NOT say?"
        helper="Optional guardrails, like never quoting exact prices."
        placeholder="e.g. Never promise same-day installs outside Austin."
        value={answers.guardrails}
        onChange={(v) => updateField('guardrails', v)}
        rows={2}
      />
    </div>
  );
}

export default function KnowledgeBaseLibrary() {
  const { selectedWorkspaceId } = useWorkspace();
  const isMobile = useIsMobile();
  const { showToast } = useToast();

  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [viewingKb, setViewingKb] = useState(null);
  const [editingKb, setEditingKb] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editChatContent, setEditChatContent] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createMode, setCreateMode] = useState('guided');
  const [createName, setCreateName] = useState('');
  const [createUrlInput, setCreateUrlInput] = useState('');
  const [createUrls, setCreateUrls] = useState([]);
  const [createContent, setCreateContent] = useState('');
  const [createChatContent, setCreateChatContent] = useState('');
  const [guidedAnswers, setGuidedAnswers] = useState(emptyGuidedAnswers);
  const [scraping, setScraping] = useState(false);
  const [creating, setCreating] = useState(false);
  const [scrapedUrls, setScrapedUrls] = useState([]);
  const [pullingLinkId, setPullingLinkId] = useState('');

  const createNameRef = useRef(null);

  const loadKnowledgeBases = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setKnowledgeBases([]);
      setLoading(false);
      setLoadError(false);
      return;
    }

    setLoading(true);
    setLoadError(false);
    try {
      const [kbSnap, formsSnap, chatSnap] = await Promise.all([
        getDocs(query(collection(db, 'knowledge_bases'), where('workspaceId', '==', selectedWorkspaceId))),
        getDocs(query(collection(db, 'forms'), where('workspaceId', '==', selectedWorkspaceId))),
        getDocs(query(collection(db, 'chat_configs'), where('workspaceId', '==', selectedWorkspaceId))),
      ]);

      const formsByKb = {};
      formsSnap.forEach((d) => {
        const data = d.data();
        const kbId = data.settings?.knowledgeBaseId;
        if (!kbId) return;
        if (!formsByKb[kbId]) formsByKb[kbId] = [];
        formsByKb[kbId].push({ id: d.id, name: data.name || 'Unnamed form' });
      });

      const chatsByKb = {};
      chatSnap.forEach((d) => {
        const data = d.data();
        const kbId = data.knowledgeBaseId;
        if (!kbId) return;
        if (!chatsByKb[kbId]) chatsByKb[kbId] = [];
        chatsByKb[kbId].push({ id: d.id, label: data.label || 'Chat widget' });
      });

      const list = [];
      kbSnap.forEach((d) => {
        const data = d.data();
        list.push({
          id: d.id,
          ...data,
          formsUsing: formsByKb[d.id] || [],
          chatsUsing: chatsByKb[d.id] || [],
        });
      });

      list.sort((a, b) => {
        const aMs = (a.updatedAt || a.createdAt)?.toDate?.()?.getTime?.() ?? 0;
        const bMs = (b.updatedAt || b.createdAt)?.toDate?.()?.getTime?.() ?? 0;
        return bMs - aMs;
      });

      setKnowledgeBases(list);
    } catch (e) {
      console.error('Error loading knowledge bases:', e);
      setLoadError(true);
      setKnowledgeBases([]);
    } finally {
      setLoading(false);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    loadKnowledgeBases();
  }, [loadKnowledgeBases]);

  useEffect(() => {
    if (showCreateModal && createNameRef.current) {
      createNameRef.current.focus();
    }
  }, [showCreateModal]);

  const closeViewModal = useCallback(() => {
    setViewingKb(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setEditingKb(null);
    setEditContent('');
    setEditChatContent('');
  }, []);

  const closeCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setCreateMode('guided');
    setCreateName('');
    setCreateUrlInput('');
    setCreateUrls([]);
    setCreateContent('');
    setCreateChatContent('');
    setGuidedAnswers(emptyGuidedAnswers());
    setScrapedUrls([]);
    setPullingLinkId('');
  }, []);

  useModalEscape(closeViewModal, !!viewingKb);
  useModalEscape(closeEditModal, !!editingKb);
  useModalEscape(closeCreateModal, showCreateModal);

  function openViewModal(kb) {
    setViewingKb(kb);
  }

  function openEditModal(kb) {
    setEditingKb(kb);
    setEditContent(kb.content || '');
    setEditChatContent(kb.chatContent || '');
  }

  function openCreateModal() {
    setShowCreateModal(true);
    setCreateMode('guided');
    setCreateName('');
    setCreateUrlInput('');
    setCreateUrls([]);
    setCreateContent('');
    setCreateChatContent('');
    setGuidedAnswers(emptyGuidedAnswers());
    setScrapedUrls([]);
    setPullingLinkId('');
  }

  async function handleSaveEdit() {
    if (!editingKb) return;
    setSavingEdit(true);
    try {
      await updateDoc(doc(db, 'knowledge_bases', editingKb.id), {
        content: editContent.trim(),
        chatContent: (editChatContent || '').trim(),
        updatedAt: new Date(),
      });
      showToast('Knowledge base saved');
      closeEditModal();
      await loadKnowledgeBases();
    } catch (e) {
      console.error('Error saving knowledge base:', e);
      showToast('Error saving knowledge base', 'error');
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleScrape() {
    const urlsToScrape = createUrls.length > 0
      ? createUrls
      : createUrlInput.trim() ? [createUrlInput.trim()] : [];

    if (urlsToScrape.length === 0) {
      showToast('Please add at least one URL', 'info');
      return;
    }

    setScraping(true);
    try {
      let combinedContent = '';
      const successfulUrls = [];

      for (let i = 0; i < urlsToScrape.length; i += 1) {
        const url = urlsToScrape[i];
        const result = await scrapeWebsite(url, { fullContent: true, workspaceId: selectedWorkspaceId });
        if (!result.success) {
          console.warn(`Failed to scrape ${url}:`, result.error);
          continue;
        }
        const content = result.businessContext || '';
        if (!content.trim()) continue;

        if (combinedContent) combinedContent += '\n\n---\n\n';
        combinedContent += `[From: ${url}]\n\n${content}`;
        successfulUrls.push(url);
      }

      if (!combinedContent.trim()) throw new Error('No content found on those pages');

      setCreateContent(combinedContent);
      setScrapedUrls(successfulUrls);
      showToast(`Scraped ${successfulUrls.length} page${successfulUrls.length === 1 ? '' : 's'}. Review before creating.`);
    } catch (e) {
      console.error('Error scraping URLs:', e);
      showToast(e.message || 'Error scraping URLs', 'error');
    } finally {
      setScraping(false);
    }
  }

  async function handlePullReferenceLink(linkId, url) {
    if (!url.trim()) return;
    setPullingLinkId(linkId);
    try {
      const result = await scrapeWebsite(url, { fullContent: true, workspaceId: selectedWorkspaceId });
      if (!result.success) throw new Error(result.error || 'Could not pull content from that URL');
      const content = result.businessContext || '';
      if (!content.trim()) throw new Error('No content found on that page');

      setGuidedAnswers((prev) => ({
        ...prev,
        referenceLinks: prev.referenceLinks.map((link) => (
          link.id === linkId ? { ...link, pulledContent: content } : link
        )),
      }));
      showToast('Content pulled. It will appear in Reference material.');
    } catch (e) {
      console.error('Error pulling reference link:', e);
      showToast(e.message || 'Error pulling content', 'error');
    } finally {
      setPullingLinkId('');
    }
  }

  function getResolvedCreateContent() {
    if (createMode === 'guided') return assembleGuidedContent(guidedAnswers).trim();
    return createContent.trim();
  }

  function getCreateSources() {
    if (createMode === 'guided') {
      return (guidedAnswers.referenceLinks || [])
        .map((link) => link.url?.trim())
        .filter(Boolean)
        .map((url) => ({ type: 'website', url, addedAt: new Date() }));
    }
    if (createMode === 'urls') {
      return scrapedUrls.map((url) => ({ type: 'website', url, addedAt: new Date() }));
    }
    return [];
  }

  async function handleCreate() {
    const content = getResolvedCreateContent();
    if (!selectedWorkspaceId || !createName.trim() || !content) return;
    setCreating(true);
    try {
      await addDoc(collection(db, 'knowledge_bases'), {
        workspaceId: selectedWorkspaceId,
        name: createName.trim(),
        content,
        chatContent: (createChatContent || '').trim(),
        isShared: false,
        formsUsing: [],
        sources: getCreateSources(),
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      showToast('Knowledge base created');
      closeCreateModal();
      await loadKnowledgeBases();
    } catch (e) {
      console.error('Error creating knowledge base:', e);
      showToast('Error creating knowledge base', 'error');
    } finally {
      setCreating(false);
    }
  }

  const pagePadding = isMobile ? '1rem 1rem 2rem' : '24px 32px 48px';
  const resolvedCreateContent = getResolvedCreateContent();
  const canCreate = createName.trim().length > 0 && resolvedCreateContent.length > 0;

  return (
    <div className="console-shell" style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      <div className="today-page" style={{ padding: pagePadding }}>
        <div className="today-header">
          <div className="today-header-left" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
              <h1 className="today-h1">Knowledge Base</h1>
              {!loading && knowledgeBases.length > 0 && (
                <span className="today-usage-pill">
                  {knowledgeBases.length} knowledge base{knowledgeBases.length === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {selectedWorkspaceId && (
              <p style={{ margin: 0, color: 'var(--muted)', fontSize: '0.9rem', lineHeight: 1.5, maxWidth: '560px' }}>
                {SUBTITLE}
              </p>
            )}
          </div>
          {selectedWorkspaceId && (
            <button
              type="button"
              className="inbox-pill-btn-primary"
              onClick={openCreateModal}
              style={{ flexShrink: 0 }}
            >
              + New knowledge base
            </button>
          )}
        </div>

        {!selectedWorkspaceId && (
          <div className="today-access-card">
            <p style={{ margin: 0, color: 'var(--muted)' }}>Select a workspace to view knowledge bases.</p>
          </div>
        )}

        {selectedWorkspaceId && loading && <KbTableSkeleton />}

        {selectedWorkspaceId && loadError && (
          <div className="today-load-error">Could not load knowledge bases. Refresh to retry.</div>
        )}

        {selectedWorkspaceId && !loading && !loadError && knowledgeBases.length === 0 && (
          <div className="today-access-card">
            <h2 style={{ marginTop: 0, color: 'var(--ink)' }}>Build your first knowledge base</h2>
            <p style={{ color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
              {SUBTITLE}
            </p>
            <button
              type="button"
              className="inbox-pill-btn-primary"
              onClick={openCreateModal}
            >
              + New knowledge base
            </button>
          </div>
        )}

        {selectedWorkspaceId && !loading && !loadError && knowledgeBases.length > 0 && (
          isMobile ? (
            <div className="fb-kb-list">
              {knowledgeBases.map((kb) => {
                const formCount = kb.formsUsing.length;
                const chatCount = kb.chatsUsing.length;
                const hasChatContent = typeof kb.chatContent === 'string' && kb.chatContent.trim().length > 0;
                const isUnused = formCount + chatCount === 0;

                return (
                  <div key={kb.id} className="fb-kb-list-row">
                    <div className="fb-kb-list-name">
                      {kb.name || 'Unnamed knowledge base'}
                    </div>
                    <div className="fb-kb-list-chips">
                      <KbUsageChips
                        formCount={formCount}
                        chatCount={chatCount}
                        isUnused={isUnused}
                        hasChatContent={hasChatContent}
                      />
                    </div>
                    <div className="fb-kb-list-updated">
                      Updated {formatTimestamp(kb.updatedAt ?? kb.createdAt)}
                    </div>
                    <div className="fb-kb-list-actions">
                      <KbRowActions kb={kb} onView={openViewModal} onEdit={openEditModal} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="fb-table-wrap">
              <table className="fb-table">
                <colgroup>
                  <col className="fb-table-col-name" />
                  <col className="fb-table-col-usage" />
                  <col className="fb-table-col-updated" />
                  <col className="fb-table-col-actions" />
                </colgroup>
                <thead>
                  <tr>
                    <th className="fb-table-head-cell" scope="col">Name</th>
                    <th className="fb-table-head-cell" scope="col">Usage</th>
                    <th className="fb-table-head-cell" scope="col">Updated</th>
                    <th className="fb-table-head-cell fb-table-head-actions" scope="col" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {knowledgeBases.map((kb) => {
                    const formCount = kb.formsUsing.length;
                    const chatCount = kb.chatsUsing.length;
                    const hasChatContent = typeof kb.chatContent === 'string' && kb.chatContent.trim().length > 0;
                    const isUnused = formCount + chatCount === 0;
                    const summary = kbSummary(kb.content);

                    return (
                      <tr key={kb.id} className="fb-table-row">
                        <td className="fb-table-cell">
                          <div className="fb-table-kb-name">
                            {kb.name || 'Unnamed knowledge base'}
                          </div>
                          {summary !== 'No content yet' && (
                            <div className="fb-table-kb-summary">{summary}</div>
                          )}
                        </td>
                        <td className="fb-table-cell">
                          <div className="fb-table-cell-chips">
                            <KbUsageChips
                              formCount={formCount}
                              chatCount={chatCount}
                              isUnused={isUnused}
                              hasChatContent={hasChatContent}
                            />
                          </div>
                        </td>
                        <td className="fb-table-cell fb-table-cell-updated">
                          Updated {formatTimestamp(kb.updatedAt ?? kb.createdAt)}
                        </td>
                        <td className="fb-table-cell fb-table-cell-actions">
                          <div className="fb-table-actions">
                            <KbRowActions kb={kb} onView={openViewModal} onEdit={openEditModal} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {viewingKb && (
        <KbViewModal kb={viewingKb} onClose={closeViewModal} />
      )}

      {editingKb && (
        <div
          className="fb-modal-overlay"
          role="presentation"
          onClick={closeEditModal}
        >
          <div
            className="fb-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-edit-title"
            style={{ maxWidth: '720px', width: '90%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fb-modal-header">
              <h2 id="kb-edit-title" className="fb-modal-title">
                {editingKb.name || 'Unnamed knowledge base'}
              </h2>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="kb-edit-content" className="fb-label">Email and spam knowledge</label>
              <textarea
                id="kb-edit-content"
                className="fb-textarea"
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                rows={12}
                style={{ marginTop: '0.5rem', maxHeight: '40vh' }}
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="kb-edit-chat-content" className="fb-label">Chat-specific knowledge</label>
              <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Leave empty and chat will use the knowledge above.
              </p>
              <textarea
                id="kb-edit-chat-content"
                className="fb-textarea"
                value={editChatContent}
                onChange={(e) => setEditChatContent(e.target.value)}
                rows={6}
                style={{ maxHeight: '30vh' }}
              />
            </div>

            <div className="fb-actions-row">
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={handleSaveEdit}
                disabled={savingEdit}
                style={{ opacity: savingEdit ? 0.6 : 1, cursor: savingEdit ? 'not-allowed' : 'pointer' }}
              >
                {savingEdit ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={closeEditModal}
                disabled={savingEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreateModal && (
        <div
          className="fb-modal-overlay"
          role="presentation"
          onClick={closeCreateModal}
        >
          <div
            className="fb-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="kb-create-title"
            style={{ maxWidth: '720px', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="fb-modal-header">
              <h2 id="kb-create-title" className="fb-modal-title">New knowledge base</h2>
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label htmlFor="kb-create-name" className="fb-label">Name</label>
              <input
                id="kb-create-name"
                ref={createNameRef}
                type="text"
                className="fb-input"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g. Main business knowledge"
                style={{ marginTop: '0.5rem', width: '100%' }}
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <div className="fb-segmented" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className={`fb-segmented-btn${createMode === 'guided' ? ' active' : ''}`}
                  onClick={() => setCreateMode('guided')}
                >
                  Guided (recommended)
                </button>
                <button
                  type="button"
                  className={`fb-segmented-btn${createMode === 'urls' ? ' active' : ''}`}
                  onClick={() => setCreateMode('urls')}
                >
                  From URLs
                </button>
                <button
                  type="button"
                  className={`fb-segmented-btn${createMode === 'paste' ? ' active' : ''}`}
                  onClick={() => setCreateMode('paste')}
                >
                  Paste content
                </button>
              </div>

              {createMode === 'guided' && (
                <div>
                  <GuidedCreateForm
                    answers={guidedAnswers}
                    setAnswers={setGuidedAnswers}
                    pullingLinkId={pullingLinkId}
                    onPullLink={handlePullReferenceLink}
                  />
                  {hasGuidedContent(guidedAnswers) && (
                    <div className="fb-callout info" style={{ marginTop: '0.5rem' }}>
                      <p className="fb-label" style={{ marginBottom: '0.75rem' }}>Preview</p>
                      <KbSectionedBody content={assembleGuidedContent(guidedAnswers)} excludeSources />
                    </div>
                  )}
                </div>
              )}

              {createMode === 'urls' && (
                <div>
                  <label htmlFor="kb-create-url" className="fb-label">URLs</label>
                  <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.75rem' }}>
                    Add any URL: your site, a news article, a press page, or anything that describes you.
                  </p>
                  <div className={`fb-url-row${isMobile ? '' : ''}`}>
                    <div className={`fb-url-input-group${isMobile ? ' is-mobile' : ''}`}>
                      <input
                        id="kb-create-url"
                        type="url"
                        className="fb-input"
                        value={createUrlInput}
                        onChange={(e) => setCreateUrlInput(e.target.value)}
                        placeholder="https://yourbusiness.com"
                        style={{ flex: 1, minWidth: 0 }}
                        disabled={scraping}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && createUrlInput.trim()) {
                            setCreateUrls((prev) => [...prev, createUrlInput.trim()]);
                            setCreateUrlInput('');
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="inbox-pill-btn-primary"
                        onClick={() => {
                          if (!createUrlInput.trim()) return;
                          setCreateUrls((prev) => [...prev, createUrlInput.trim()]);
                          setCreateUrlInput('');
                        }}
                        disabled={!createUrlInput.trim() || scraping}
                        style={{ flexShrink: 0, opacity: createUrlInput.trim() ? 1 : 0.6 }}
                      >
                        Add
                      </button>
                    </div>
                    {createContent.trim() ? (
                      <InlineConfirm
                        message="Replace existing content?"
                        confirmLabel="Replace"
                        onConfirm={handleScrape}
                        loading={scraping}
                        loadingLabel="Scraping..."
                        disabled={scraping || (createUrls.length === 0 && !createUrlInput.trim())}
                        buttonLabel={scraping
                          ? `Scraping ${createUrls.length || 1}...`
                          : `Scrape (${createUrls.length || (createUrlInput.trim() ? 1 : 0)} page${(createUrls.length || 1) > 1 ? 's' : ''})`}
                        buttonClassName="inbox-pill-btn-primary"
                        buttonStyle={{
                          minHeight: isMobile ? 44 : undefined,
                          whiteSpace: 'nowrap',
                          opacity: scraping || (createUrls.length === 0 && !createUrlInput.trim()) ? 0.55 : 1,
                          cursor: scraping || (createUrls.length === 0 && !createUrlInput.trim()) ? 'not-allowed' : 'pointer',
                          ...(isMobile ? { width: '100%', flexBasis: '100%' } : {}),
                        }}
                      />
                    ) : (
                      <button
                        type="button"
                        className="inbox-pill-btn-primary"
                        onClick={handleScrape}
                        disabled={scraping || (createUrls.length === 0 && !createUrlInput.trim())}
                        style={{
                          minHeight: isMobile ? 44 : undefined,
                          whiteSpace: 'nowrap',
                          opacity: scraping || (createUrls.length === 0 && !createUrlInput.trim()) ? 0.55 : 1,
                          cursor: scraping || (createUrls.length === 0 && !createUrlInput.trim()) ? 'not-allowed' : 'pointer',
                          ...(isMobile ? { width: '100%', flexBasis: '100%' } : {}),
                        }}
                      >
                        {scraping
                          ? `Scraping ${createUrls.length || 1}...`
                          : `Scrape (${createUrls.length || (createUrlInput.trim() ? 1 : 0)} page${(createUrls.length || 1) > 1 ? 's' : ''})`}
                      </button>
                    )}
                  </div>

                  {createUrls.length > 0 && (
                    <div className="fb-callout info" style={{ marginTop: '0.75rem' }}>
                      <div className="fb-label" style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        URLs to scrape ({createUrls.length}):
                      </div>
                      {createUrls.map((url, index) => (
                        <div key={`${url}-${index}`} className="fb-list-row">
                          <span style={{ fontSize: '0.85rem', color: 'var(--ink)', wordBreak: 'break-all' }}>{url}</span>
                          <button
                            type="button"
                            className="fb-icon-remove"
                            onClick={() => setCreateUrls(createUrls.filter((_, i) => i !== index))}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {createContent && scrapedUrls.length > 0 && (
                    <div style={{ marginTop: '1rem' }}>
                      <div className="fb-callout info" style={{ marginBottom: '0.75rem' }}>
                        Scraped from {scrapedUrls.length} page{scrapedUrls.length === 1 ? '' : 's'}
                      </div>
                      <label htmlFor="kb-create-scraped-preview" className="fb-label">Review and edit content</label>
                      <textarea
                        id="kb-create-scraped-preview"
                        className="fb-textarea"
                        value={createContent}
                        onChange={(e) => setCreateContent(e.target.value)}
                        rows={10}
                        style={{ marginTop: '0.5rem', maxHeight: '35vh' }}
                      />
                    </div>
                  )}
                </div>
              )}

              {createMode === 'paste' && (
                <div>
                  <label htmlFor="kb-create-paste-content" className="fb-label">Knowledge content</label>
                  <textarea
                    id="kb-create-paste-content"
                    className="fb-textarea"
                    value={createContent}
                    onChange={(e) => setCreateContent(e.target.value)}
                    rows={12}
                    placeholder="Paste your business knowledge here..."
                    style={{ marginTop: '0.5rem', maxHeight: '40vh' }}
                  />
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label htmlFor="kb-create-chat-content" className="fb-label">Chat-specific knowledge (optional)</label>
              <p className="fb-muted" style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.5rem' }}>
                Leave empty and chat will use the knowledge above.
              </p>
              <textarea
                id="kb-create-chat-content"
                className="fb-textarea"
                value={createChatContent}
                onChange={(e) => setCreateChatContent(e.target.value)}
                rows={4}
              />
            </div>

            <div className="fb-actions-row">
              <button
                type="button"
                className="inbox-pill-btn-primary"
                onClick={handleCreate}
                disabled={creating || !canCreate}
                style={{
                  opacity: creating || !canCreate ? 0.6 : 1,
                  cursor: creating || !canCreate ? 'not-allowed' : 'pointer',
                }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                className="inbox-pill-btn"
                onClick={closeCreateModal}
                disabled={creating || scraping}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
